/**
 * `<StaffCustomersRoute>` — `/staff/customers`.
 *
 * The FDE / internal-staff console: a searchable, paginated directory of every
 * customer organization and its workspaces. Clicking a workspace enters it via
 * the normal switch flow (`navigate('/w/:slug')` → `WorkspaceProvider` rotates
 * the session). Top-level route (NOT under `/w/:slug`), so it renders its own
 * chrome rather than the workspace shell's `PageShell`.
 *
 * Gating is server-side (`PlatformStaffGuard` on the API); the `platform:staff`
 * check here is UX only — a non-staff user is redirected home.
 */

import { useAuth } from "@cometchat-ai/auth/client";
import { Input, Spinner } from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronRight, Search, Users } from "../components/icons";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { CursorPaginationBar, useCursorPager } from "../components/cursor-pagination";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { listStaffOrganizations, type StaffWorkspace } from "../lib/api/staff-directory";

function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function StaffCustomersRoute() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search.trim());

  const pager = useCursorPager();
  useEffect(() => {
    pager.reset();
  }, [debouncedSearch, pager.reset]);

  const isStaff = auth.permissions.includes("platform:staff");

  const query = useQuery({
    queryKey: ["staff", "organizations", debouncedSearch, pager.cursor] as const,
    queryFn: () =>
      listStaffOrganizations({
        search: debouncedSearch === "" ? undefined : debouncedSearch,
        cursor: pager.cursor,
        limit: 20,
      }),
    enabled: isStaff,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const orgs = useMemo(() => query.data?.items ?? [], [query.data]);

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (auth.status !== "authenticated" || !isStaff) {
    return <Navigate to="/" replace />;
  }

  const enterWorkspace = (ws: StaffWorkspace) => navigate(`/w/${ws.slug}`);

  return (
    <div className="mx-auto min-h-screen w-full max-w-4xl px-4 py-8" data-testid="staff-customers">
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-2.5">
          <div className="min-w-0 space-y-1">
            <h1 className="text-base font-semibold tracking-[0.01em] text-foreground">
              Staff console
            </h1>
            <p className="text-sm text-muted-foreground">
              Search every customer organization and open a workspace to build or support its agent.
            </p>
          </div>
          <div className="relative w-64">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations or workspaces"
              aria-label="Search organizations or workspaces"
              data-testid="staff-customers-search"
              className="h-8 w-full rounded-md pl-8 text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
            />
          </div>
        </div>

        {query.isError ? (
          <div className="px-6 py-12">
            <ErrorState
              message="Couldn't load the customer directory."
              onRetry={query.refetch}
              data-testid="staff-customers-error"
            />
          </div>
        ) : orgs.length === 0 && !query.isPending ? (
          <div className="px-6 py-12">
            <EmptyState
              data-testid="staff-customers-empty"
              icon={debouncedSearch ? Search : Building2}
              title={debouncedSearch ? "No matches" : "No organizations yet"}
              description={
                debouncedSearch
                  ? "No organization or workspace matches your search."
                  : "Customer organizations will appear here as they sign up."
              }
            />
          </div>
        ) : (
          <>
            <ul data-testid="staff-customers-list">
              {orgs.map((org) => (
                <li key={org.id} className="border-b border-border last:border-b-0">
                  <div className="flex items-center gap-2 bg-muted px-6 py-2.5">
                    <Building2
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="truncate text-[13px] font-medium text-foreground">{org.name}</p>
                    <span className="truncate text-xs text-muted-foreground">{org.slug}</span>
                  </div>
                  <ul>
                    {org.workspaces.map((ws) => (
                      <li key={ws.id}>
                        <button
                          type="button"
                          onClick={() => enterWorkspace(ws)}
                          data-testid={`staff-workspace-${ws.slug}`}
                          className="group/ws flex w-full items-center justify-between gap-3 border-b border-border px-6 py-2.5 text-left transition-colors last:border-b-0 hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)] focus:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)] focus:outline-none"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-[13px] text-foreground">{ws.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {ws.slug} · {ws.environment}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-3 text-muted-foreground">
                            <span
                              className="flex items-center gap-1 text-[13px] tabular-nums"
                              title="members"
                            >
                              <Users className="h-3.5 w-3.5" aria-hidden="true" />
                              {ws.memberCount}
                            </span>
                            <ChevronRight
                              className="h-4 w-4 transition-transform group-hover/ws:translate-x-0.5"
                              aria-hidden="true"
                            />
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            <div className="border-t border-border px-6 py-3">
              <CursorPaginationBar
                data-testid="staff-customers-pagination"
                pageIndex={pager.pageIndex}
                canPrev={pager.canPrev}
                canNext={Boolean(query.data?.nextCursor)}
                busy={query.isFetching}
                onPrev={pager.toPrev}
                onNext={() => pager.toNext(query.data?.nextCursor)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
